import { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import styled from "styled-components";
import StudyNoticeSheet from "./StudyNoticeSheet";
import FormButton from "../../../atoms/FormButton";
import { FormGroup, StyledTextField } from "../../../atoms/LoginTextField";
import VeriticalCenter from "../../../atoms/VerticalCenter";
import Container from "../../../atoms/Container";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { UserStatus, WelcomeContentEnum, WelcompeStepEnum } from "../../../../types/welcome";
import { useResonanceLayout } from '../../ResonanceContext/ResonanceLayoutContext';
import EmailLoginFields from "../../../molecules/Home/Login/EmailLoginFields";
import SecretQuestionsForm from "../LoginJourney/SecretQuestionsForm";
import { IdentificationMethodEnum, isPasswordSignInMethod, ProfileAccessType } from "../../../../types/profile";

// Fallback so a transient/malformed role (e.g. a stale cached welcome) can never crash the
// landing on `access.signInMode`. Renders a no-op login state until the welcome revalidates.
const EMPTY_ACCESS: ProfileAccessType = {
  signInMode: IdentificationMethodEnum.NO_SIGNIN,
  fields: [],
  constraints: [],
  secretQuestions: [],
};
import useToast from "../../../../hooks/useToast";
import { useStudySlug } from "../../../../hooks/useStudySlug";
import { getDefaultTabPath } from "../../../../navigation/studyTabs";
import AuthContext from "../../../../contexts/AuthContext";
import { useConfig } from "../../../../contexts/ConfigContext";
import { login } from "../../../../services/user.service";
import { useIonRouter } from '@ionic/react';
import {
  getPasswordSignInPlaceholder,
  getRoleAccessEnabledIdentifierKeys
} from "../../../../helper/roleAccess";
import { getProjectConfig } from "../../../../projects";
// Per-project welcome-copy resolver. Reads project.welcome[language] (falling back
// through the base language, a "default" entry, then any authored language),
// interpolates {{appName}}, and — when the project declares NOTHING — falls back to
// the shared t("home_welcome") / t("home_description") wording so every current
// deployment is unchanged. Returns { badge?, title, tagline, note? }.
import { resolveWelcomeCopy } from "../../../../helper/welcomeCopy";
// Uniform themed page tint shared with the registration-success screen (see the
// constant's doc for why it must be FLAT, not a gradient).
import { WELCOME_BACKGROUND } from "../../../../theme/welcomeBackground";

const DisplayHasAccount = (): ReactElement | null => {
  const { t, i18n } = useTranslation();

  const { welcomeData, setCurrentStep, createUser, signupSubjects, isRoleLockedByEntry } = useHomeContext();
  const userStatus = welcomeData.invitations?.to_user?.user_status as string;

  const study_slug = useStudySlug();
  const { startSession, user, isAuthenticated } = useContext(AuthContext);
  const present = useToast();
  const studyId = welcomeData.id;
  const { selectedRole, app } = useConfig();
  // Use the role chosen from the entry URL (subject / investigator / intervenor),
  // not always roles[0] (the subject). Falls back to roles[0] until selection settles.
  const role = selectedRole ?? welcomeData?.roles?.[0];
  const roleId = role?.id;
  const ionRouter = useIonRouter();
  const invitation_token = new URLSearchParams(window.location.search).get("invitation");
  const isActive = userStatus === UserStatus.ACTIVE;
  const invitSignOnly = !role?.attributes?.can_sign_up_only_with_invitation || invitation_token;

  const access = role?.access ?? EMPTY_ACCESS;
  const method = access.signInMode;
  const identification_questions = access.secretQuestions;
  const enabledIdentifierKeys = useMemo(
    () => getRoleAccessEnabledIdentifierKeys(access),
    [access]
  );
  const isPasswordMethod = isPasswordSignInMethod(method);
  const isEmailOnlyPasswordSignIn = isPasswordMethod
    && enabledIdentifierKeys.length === 1
    && enabledIdentifierKeys[0] === 'email';
  const loginPlaceholder = useMemo(
    () => getPasswordSignInPlaceholder(access, t, i18n.resolvedLanguage ?? i18n.language),
    [access, i18n.language, i18n.resolvedLanguage, t]
  );
  const { setResonanceLayout } = useResonanceLayout();

  // Per-project chrome + landing copy (white-label). The resolver falls back to the
  // shared t("home_welcome")/t("home_description") wording when a project sets
  // nothing, so untouched deployments read exactly as before.
  const project = useMemo(() => getProjectConfig(study_slug), [study_slug]);
  const welcomeCopy = useMemo(
    () => resolveWelcomeCopy(project, i18n.resolvedLanguage ?? i18n.language, t, app?.name),
    [project, i18n.resolvedLanguage, i18n.language, t, app?.name]
  );
  // Institutional backing strip — rendered only when the active project supplies
  // logos (Mental+ keeps its five; Outborn / default show none).
  const partnerLogos = project.partnerLogos;
  // i18n key -> resolved caption; literal `caption` as fallback. Never accent-tinted.
  const partnerCaption = partnerLogos?.captionKey
    ? t(partnerLogos.captionKey)
    : partnerLogos?.caption;
  const hasAuthForm =
    isPasswordMethod || method === IdentificationMethodEnum.SECRET_QUESTIONS;
  // Same visibility rule the standalone sign-up button always had — only its
  // PLACEMENT changed (footer of the login card, under an "Êtes-vous nouveau ?"
  // divider, so it no longer competes with the Se connecter CTA).
  const showRegister = invitSignOnly && !isActive;

  // Public information notice: the welcome payload carries the flagged content's ID only
  // (never its body), and the key is absent from an older API build / a welcome cached
  // before this feature — hence the full optional chaining, which degrades to no link.
  // Local state only: opening the sheet must NOT push a step, or the login fields the
  // visitor already typed into would be unmounted and goPrevious would gain a phantom entry.
  const publicNoticeId = welcomeData?.contents?.[WelcomeContentEnum.PUBLIC_NOTICE]?.id ?? null;
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);


  // useEffect(() => {
  //   // mark this page as “welcome” while it is mounted
  //   if (!isAuthenticated){
  //     setResonanceLayout({ title: "", disableNav:true, welcomePage: true, disableHeader: false, backgroundColor: "white" });
  //   }


  //   // ⬇️ this runs *before* the component actually unmounts
  //   return () => {
  //     if (isAuthenticated){
  //       setResonanceLayout({ welcomePage: false, disableHeader: true, disableNav: false, backgroundColor: "white" });
  //     }

  //   };
  // }, []);               // ← same dependency list

  useEffect(() => {
    if (!isAuthenticated) {
      setResonanceLayout({
        disableHeader: false,
        disableNav: true,
        // Uniform themed tint (see WELCOME_BACKGROUND): the wave header and the
        // content below sit on the same flat color, so there is no seam / detached
        // band. This screen owns its background.
        backgroundColor: WELCOME_BACKGROUND,
        title: "",
        welcomePage: true,
      });
      return;
    }

    setResonanceLayout({
      disableHeader: true,
      disableNav: false,
      backgroundColor: "white",
      title: "",
      welcomePage: false,
    });
  }, [isAuthenticated, setResonanceLayout]);

  // if (!welcomePage) return null;

  const handleConnect = async (body: any) => {
    try {
      const { data } = await login(studyId, roleId, { ...body, invitation_token });
      // Redirect with the role startSession RESOLVED (the user's real role) — the
      // landing pre-selection can be another role's, and context reads here would
      // be stale. Role-aware landing: investigators → suivi tab.
      const sessionRole = await startSession(data.token);
      ionRouter.push(getDefaultTabPath(study_slug, sessionRole?.name), 'root', 'replace', { unmount: true });
    } catch (e) {
      const status = (e as AxiosError).response?.status;
      present(
        status === 429
          ? t("home_login_too_many_attempts")
          : status === 401
            ? t("home_login_incorrect")
            : t("common_server_error_title")
      );
      console.error(e);
    }
  };

  const onAccountClick = (): void => setCurrentStep(WelcompeStepEnum.LOGIN);

  const onNewClick = (): void => {
    // No role pinned by a slug/invitation and several self-registerable subjects
    // (e.g. father/mother) → let the participant pick which one, then register as it.
    if (!isRoleLockedByEntry && signupSubjects.length > 1) {
      setCurrentStep(WelcompeStepEnum.PROFILE_SELECTOR);
      return;
    }
    // A personal invitation (PENDING_UNIQUE) pre-creates the user WITHOUT credentials.
    // Normal personal invitations (investigator / intervenor / subject) are pre-created as
    // PROSPECT — route them through the usual signup flow (createUser → onboarding →
    // eligibility → signup → stratification (subject-only) → consent → registration) instead
    // of the set-password-only page; the password is set when consent is submitted.
    // Direct inclusion (INCLUSION_EN_ATTENTE_DE_VALIDATION), public links (no to_user) and
    // no-sign-in roles keep their existing path.
    const isPersonalInvitation = !!welcomeData?.invitations?.to_user?.id;
    const needsCredentials = isPasswordMethod || method === IdentificationMethodEnum.SECRET_QUESTIONS;
    const isProspectInvite = isPersonalInvitation && userStatus === UserStatus.PROSPECT;
    if (invitation_token && isPersonalInvitation && needsCredentials && !isProspectInvite) {
      setCurrentStep(WelcompeStepEnum.INIT_IDENTIFICATION);
    } else {
      createUser(role);
    }
  };


  return (
    <Container>
      <VeriticalCenter
        style={{
          justifyContent: "flex-start",
          minHeight: "80vh",
          maxHeight: "1000px", // prevents excessive spacing on big screens
          margin: "0 auto",
        }}
      >
        {/*
          One flat child list, two layouts. On phones the wrapper columns are
          `display: contents` (they vanish) so the five items flow in a single
          column in the visual order Hero → Panel → Note → CTA → Backing (set via
          `order`). From ≥960px the wrappers become real columns: intro text on the
          left, the login card + register CTA on the right — halving the height so
          the CTA is visible without scrolling and using the otherwise-empty desktop
          width. Conditional items (e.g. Outborn's absent logo strip) leave no gap.
        */}
        <Stack>
          <ColIntro>
            <Hero>
              {welcomeCopy.badge && <Badge>{welcomeCopy.badge}</Badge>}
              <ScreenTitle>{welcomeCopy.title}</ScreenTitle>
              <Tagline>{welcomeCopy.tagline}</Tagline>
            </Hero>

            {welcomeCopy.note && <Note>{welcomeCopy.note}</Note>}

            {partnerLogos && partnerLogos.logos.length > 0 && (
              <Backing>
                {partnerCaption && <BackingCaption>{partnerCaption}</BackingCaption>}
                <LogoStrip>
                  {partnerLogos.logos.map((src, i) => (
                    <PartnerLogo key={i} src={src} alt="" loading="lazy" />
                  ))}
                </LogoStrip>
              </Backing>
            )}
          </ColIntro>

          <ColAction>
            {hasAuthForm && (
              <Panel>
                {isPasswordMethod && (
                  <EmailLoginFields
                    onConnect={handleConnect}
                    withNickName={!isEmailOnlyPasswordSignIn}
                    loginPlaceholder={loginPlaceholder}
                  />
                )}
                {method === IdentificationMethodEnum.SECRET_QUESTIONS && (
                  <SecretQuestionsForm
                    onConnect={handleConnect}
                    secretQuestions={identification_questions}
                  />
                )}

                {/* Sign-up folded into the card footer: a hairline divider with the
                    app's own "new here?" wording, then a QUIET tonal button — so the
                    filled Se connecter above stays the single loud CTA. */}
                {showRegister && (
                  <>
                    <NewHereDivider>
                      <span>{t("home_is_new")}</span>
                    </NewHereDivider>
                    <RegisterButton type="button" onClick={onNewClick}>
                      {welcomeCopy.registerLabel ?? t("home_has_account_false")}
                    </RegisterButton>
                  </>
                )}
              </Panel>
            )}

            {/* Roles with no login form (NO_SIGNIN): sign-up is the only action, so
                it stands alone — filled secondary, but compact, never a slab. */}
            {!hasAuthForm && showRegister && (
              <SoloRegisterButton type="button" onClick={onNewClick}>
                {welcomeCopy.registerLabel ?? t("home_has_account_false")}
              </SoloRegisterButton>
            )}

            {/* Reference document, offered to READ — never framed as terms accepted by
                signing up (the document to accept is the consent, at the end of signup).
                Rendered only when a researcher has flagged one. */}
            {!!publicNoticeId && (
              <NoticeLine>
                {t("home_study_notice_prefix")}{" "}
                <NoticeButton type="button" onClick={() => setIsNoticeOpen(true)}>
                  {t("home_study_notice_link")}
                </NoticeButton>
              </NoticeLine>
            )}
          </ColAction>
        </Stack>
      </VeriticalCenter>

      {/* The sheet resolves the flagged content server-side from the study id alone —
          the landing never sends a content id. Mounted only when there is one to show. */}
      {!!publicNoticeId && (
        <StudyNoticeSheet
          isOpen={isNoticeOpen}
          onDismiss={() => setIsNoticeOpen(false)}
          studyId={studyId}
        />
      )}
    </Container>
  );
};

/* ── Responsive column shell ──────────────────────────────────────────────────
   Stack is a single-column flex on phones and a two-column flex from ≥960px. The
   two wrapper columns are `display: contents` on phones (see JSX note) and become
   real columns on desktop. */

const DESKTOP = "@media (min-width: 960px)";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  /* Ionic's fullscreen content already begins below the wave header; only a small
     breathing pad is needed so the hero sits close under the nav (no dead space). */
  padding: clamp(10px, 2.5vh, 22px) 16px 40px;
  box-sizing: border-box;

  ${DESKTOP} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: 32px;
    max-width: 900px;
    /* Center the two-column block vertically in the 80vh area (VerticalCenter is
       flex-start; auto block margins win and center it) so it isn't top-loaded with
       empty space beneath. */
    margin: auto;
    padding: clamp(16px, 4vh, 48px) 24px;
  }
`;

const ColIntro = styled.div`
  display: contents;

  ${DESKTOP} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    flex: 1 1 0;
    min-width: 0;
    text-align: left;
    /* Align the intro block's optical center with the login card across the gap. */
    padding-top: 6px;
  }
`;

const ColAction = styled.div`
  display: contents;

  ${DESKTOP} {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    flex: 0 0 440px;
    max-width: 440px;
  }
`;

/* ── Structured-trust content: token-driven, palette-agnostic ─────────────────
   `order` drives the phone single-column sequence (Hero 1 → Panel 2 → Note 3 →
   CTA 4 → Backing 5); on desktop it is harmless (matches DOM order within each
   column). */

const Hero = styled.header`
  order: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: center;

  ${DESKTOP} {
    align-items: flex-start;
    text-align: left;
  }
`;

/* Small contextual pill — the ACCENT role (links/pills). Filled with accent and its
   WCAG-picked -contrast ink, so the label stays legible whether accent is a mid blue
   (Mental+) or a light sky (Outborn) — accent-as-text would fail on the light one. */
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 999px;
  background: var(--app-color-accent, #4285f4);
  color: var(--app-color-accent-contrast, #ffffff);
  font-family: var(--app-font-family, inherit);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ScreenTitle = styled.h1`
  margin: 0;
  text-align: inherit;
  font-family: var(--app-font-heading, inherit);
  font-weight: 800;
  font-size: clamp(24px, 6vw, 32px);
  line-height: 1.2;
  color: var(--app-color-text, #041723);
`;

const Tagline = styled.p`
  margin: 0;
  max-width: 520px;
  font-family: var(--app-font-family, inherit);
  font-size: clamp(15px, 4.2vw, 17px);
  line-height: 1.5;
  /* Softened text ink — a value line, not a headline. Contrast stays safe on the
     page tint because it is the text ink, never the (possibly light) accent. */
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.72);
`;

/* The auth form lives in a bordered panel raised off the page tint, with a thin
   EMPHASIS-tone bar across the top (the derived strong tone of primary — saturated
   on every palette, unlike a pastel chrome pick). Panel background = the BACKGROUND
   token (white) — deliberately NOT the surface token — so the surface-token input
   fields rendered inside EmailLoginFields stay visible against it. */
const Panel = styled.section`
  order: 2;
  position: relative;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  padding: 16px 16px 16px;
  background: var(--app-color-background, #ffffff);
  border: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  border-radius: 20px;
  box-shadow: 0 12px 34px rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  overflow: hidden;
  box-sizing: border-box;

  /* Both card CTAs share ONE explicit width so the filled Se connecter and the
     outline sign-up render as equal pills. FormButton sizes itself by padding
     (18px/80px) around the label, so two different label lengths can never come
     out equal — pinning the width (and relaxing the huge side padding so long
     labels still fit) is the only robust equalizer. Scoped to this card only;
     FormButton everywhere else is untouched. */
  --card-cta-width: min(100%, 320px);

  ${FormButton} {
    width: var(--card-cta-width);
    padding-left: 16px;
    padding-right: 16px;
  }

  /* Fields line up with the CTAs: the FormGroup atom insets them with a fixed
     50px side margin (narrower than the buttons) — swap it for the shared card
     width, centered. Scoped to this card; the atom elsewhere is untouched. */
  ${FormGroup} {
    width: var(--card-cta-width);
    margin: 15px auto 0;
    box-sizing: border-box;
  }

  /* Slightly calmer field text than the atom's 18px (input doesn't inherit the
     wrapper's font by default, so set both). */
  ${StyledTextField} {
    font-size: 15px;

    input {
      font-size: 15px;
    }
  }

  /* The "Mot de passe oublié ?" row is inline-styled in EmailLoginFields with a
     50px right pad that matched the OLD 50px field inset — realign it to the new
     shared width (padding needs !important to beat the inline style; 10px keeps
     it off the field pill's rounded corner). :not() excludes the FormGroup div. */
  form > div:not(${FormGroup}) {
    width: var(--card-cta-width);
    margin-left: auto;
    margin-right: auto;
    padding-right: 10px !important;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--app-color-emphasis, var(--app-color-primary, #2db6b4)),
      var(--app-color-emphasis-soft, var(--app-color-primary-tint, #abf0ef))
    );
  }
`;

const Note = styled.p`
  order: 3;
  margin: -4px 0 0;
  max-width: 460px;
  text-align: center;
  font-family: var(--app-font-family, inherit);
  font-size: 13.5px;
  line-height: 1.55;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);

  ${DESKTOP} {
    margin: 0;
    text-align: left;
  }
`;

/* Public-notice line. Same quiet ink / size family as Note (it is the same register:
   small print under the action column), but `order: 6` — the wrapper columns are
   `display: contents` on phones, so ALL items compete in one flat order list and 6
   puts this last, under the Backing strip. On desktop it simply follows the CTA in
   ColAction. Deliberately styled as a sentence with an inline link, not a button row:
   this revives the small legal line the landing used to carry, with READ wording. */
const NoticeLine = styled.p`
  order: 6;
  margin: 0;
  max-width: 460px;
  text-align: center;
  font-family: var(--app-font-family, inherit);
  font-size: 13px;
  line-height: 1.55;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
`;

/* A button, not an <a>: there is no public URL for the notice — it only ever opens
   the in-app sheet. Accent = the app's link family. */
const NoticeButton = styled.button`
  display: inline;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: var(--app-color-accent, #4285f4);
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.4);
    outline-offset: 2px;
  }
`;

/* "Êtes-vous nouveau ?" hairline divider inside the card footer — separates the
   returning-user form from the sign-up affordance without adding another slab. */
const NewHereDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 18px 0 14px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.1);
  }

  span {
    flex: 0 0 auto;
    font-family: var(--app-font-family, inherit);
    font-size: 12.5px;
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
    white-space: nowrap;
  }
`;

/* Sign-up button: the OUTLINE twin of the filled Se connecter above — same shared
   card-CTA width (see Panel), same 30px radius / 16px bold; vertical padding
   trimmed by the 2px border so both render the same outer height. Border in the
   ACCENT color (links/answers family) — calmer than echoing the CTA secondary.
   Label keeps the dark TEXT ink — accent-as-text would fail on Outborn's pale
   sky — so filled vs outline carries the hierarchy. */
const RegisterButton = styled.button`
  display: block;
  margin: 0 auto;
  width: var(--card-cta-width, min(100%, 320px));
  padding: 16px 14px;
  box-sizing: border-box;
  border: 2px solid var(--app-color-accent, #4285f4);
  border-radius: 30px;
  background: transparent;
  color: var(--app-color-text, #041723);
  font-family: var(--app-font-family, inherit);
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;

  &:active {
    background: rgba(var(--app-color-accent-rgb, 66, 133, 244), 0.12);
  }
  &:focus-visible {
    outline: 2px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.4);
    outline-offset: 2px;
  }
`;

/* No-login-form variant (NO_SIGNIN roles): sign-up is the ONLY action, so it takes
   the full secondary fill — FormButton geometry, same as Se connecter elsewhere. */
const SoloRegisterButton = styled.button`
  order: 4;
  display: block;
  margin: 4px auto 0;
  padding: 15px 80px;
  max-width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: 30px;
  background: var(--app-color-secondary, #fecf48);
  color: var(--app-color-secondary-contrast, #041723);
  font-family: var(--app-font-family, inherit);
  font-size: 16px;
  font-weight: bold;
  box-shadow: 0px 0px 25px -12px #000000;
  cursor: pointer;

  &:active {
    background: var(--app-color-secondary-shade, #e0b63f);
  }
  &:focus-visible {
    outline: 2px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.4);
    outline-offset: 2px;
  }
`;

/* ── Institutional backing (grafted quiet grayscale->color logo treatment) ── */

/* Framed backing card — a quiet primary-washed panel so the partner marks read as
   endorsement, not decoration. */
const Backing = styled.footer`
  order: 5;
  width: 100%;
  max-width: 460px;
  margin: 4px auto 0;
  padding: 14px 16px 16px;
  background: rgba(var(--app-color-primary-rgb, 148, 241, 239), 0.06);
  border: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  border-radius: 16px;
  box-sizing: border-box;

  ${DESKTOP} {
    margin: 0;
  }
`;

const BackingCaption = styled.div`
  margin-bottom: 12px;
  text-align: center;
  font-family: var(--app-font-family, inherit);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
`;

const LogoStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 18px;
`;

/* Grayscale + dimmed at rest so no partner mark competes with the brand; each fades
   up to its own color on hover/focus. Sized by height so mixed logos share a baseline. */
const PartnerLogo = styled.img`
  height: 30px;
  width: auto;
  max-width: 88px;
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.6;
  transition: filter 0.2s ease, opacity 0.2s ease;

  &:hover {
    filter: grayscale(0);
    opacity: 1;
  }

  @media (max-width: 480px) {
    height: 26px;
    max-width: 72px;
  }
`;

export default DisplayHasAccount;
